import { useHistory, useLocation } from 'react-router';
import { EFamilyAccountType, EQueryStringParam, ERoutes, maxNameLength } from '../util/constants';
import API from '../api/api';
import GlobalContext from './GlobalContext';
import { validatePassword } from '../util/validation/passwordValidation';
import { TCreateFamilyAccountRouterState } from './CreateFamilyAccountContext';
import { TCompleteConfirmAccountPost } from '../api/types';
import { TBusiness, TUser } from '../util/types';
import { createContext, FC, useContext, useEffect, useState } from 'react';

export enum EConfirmAccountUiState {
    default,
    routerParsed,
    queryStringParsed,
    initialized,
    ready,
    loading,
    success,
    fatalError,
    softError,
}

export type TConfirmFamilySuccessData = {
    primaryUser: TUser;
    businesses: TBusiness[];
    legacySyncToken: string;
};

export type TConfirmAccountRouterState = {
    confirmFamilyData?: TConfirmFamilySuccessData;
    prePopulateForm?: {
        userId: string;
        code: string;
        unconfirmedLinkId: string;
        firstName: string;
        lastName: string;
        email: string;
        password: string;
        countryCode: string;
        confirmingUser: TConfirmingUser;
    };
};

export type TConfirmAccountContext = {
    confirmAccountUiState: EConfirmAccountUiState;
    errorMessage: string;
    userId: string;
    firstName: string;
    setFirstName: (name: string) => void;
    firstNameIsValid: boolean;
    setFirstNameIsValid: (isValid: boolean) => void;
    firstNameValidationMessage: string;
    lastName: string;
    setLastName: (name: string) => void;
    lastNameIsValid: boolean;
    setLastNameIsValid: (isValid: boolean) => void;
    lastNameValidationMessage: string;
    email: string;
    password: string;
    setPassword: (password: string) => void;
    passwordIsValid: boolean;
    countryCode: string;
    setCountryCode: (code: string) => void;
    confirmingUser: TConfirmingUser;
    countryIsValid: boolean;
    submitConfirmAccount: () => void;
    businesses: TBusiness[];
    addFamily: () => void;
    familyFirstAddFamily: () => void;
    legacySyncToken: string;
    confirmFamilySuccessData: TConfirmFamilySuccessData;
    dataIsPrePopulated: boolean;
};

type TConfirmingUser = {
    firstName: string;
    lastName: string;
};

const defaultConfirmingUser: TConfirmingUser = { firstName: '', lastName: '' };

const ConfirmAccountContext = createContext<TConfirmAccountContext | undefined>(undefined);

export const ConfirmAccountProvider: FC = (props) => {
    const { getCoBranding, language, countriesList, getCountriesList, subscriberId, setSubscriberId } =
        useContext(GlobalContext);
    const { children } = props;
    const { search: queryParams } = useLocation();
    const [errorMessage, setErrorMessage] = useState(language.validation.genericError);
    const [confirmAccountUiState, setConfirmAccountUiState] = useState(EConfirmAccountUiState.default);
    const [userId, setUserId] = useState('');
    const [code, setCode] = useState('');
    const [unconfirmedLinkId, setUnconfirmedLinkId] = useState('');
    const [confirmingUser, setConfirmingUser] = useState<TConfirmingUser>(defaultConfirmingUser);
    const [firstName, setFirstName] = useState('');
    const [firstNameIsValid, setFirstNameIsValid] = useState(true);
    const [firstNameValidationMessage, setFirstNameValidationMessage] = useState('');
    const [lastName, setLastName] = useState('');
    const [lastNameIsValid, setLastNameIsValid] = useState(true);
    const [lastNameValidationMessage, setLastNameValidationMessage] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordIsValid, setPasswordIsValid] = useState(true);
    const [countryCode, setCountryCode] = useState('');
    const [countryIsValid, setCountryIsValid] = useState(true);
    const [businesses, setBusinesses] = useState<TBusiness[]>([]);
    const [legacySyncToken, setLegacySyncToken] = useState('');
    const [confirmFamilySuccessData, setConfirmFamilySuccessData] = useState<TConfirmFamilySuccessData>(null);
    const [dataIsPrePopulated, setDataIsPrePopulated] = useState(false);
    const history = useHistory();
    const { state: routerState }: { state: TConfirmAccountRouterState } = useLocation();

    const parseQueryString = () => {
        const params = new URLSearchParams(queryParams);
        const userId = params.get(EQueryStringParam.userId);
        const code = params.get(EQueryStringParam.code);
        const linkId = params.get(EQueryStringParam.linkId);
        const subscriberId = params.get(EQueryStringParam.subscriberId);
        userId && setUserId(userId);
        code && setCode(code);
        linkId && setUnconfirmedLinkId(linkId);
        subscriberId && setSubscriberId(subscriberId);
        if (!code || !userId || !linkId) {
            console.error(
                `Missing query string parameter(s) -- ${!code ? EQueryStringParam.code : ''} ${
                    !userId ? EQueryStringParam.userId : ''
                } ${!unconfirmedLinkId ? EQueryStringParam.linkId : ''}`,
            );
            setConfirmAccountUiState(EConfirmAccountUiState.fatalError);
        } else {
            setConfirmAccountUiState(EConfirmAccountUiState.queryStringParsed);
        }
    };

    // -------------
    // Setup
    // -------------
    const parseRouterState = () => {
        if (routerState) {
            const { confirmFamilyData, prePopulateForm } = routerState;
            if (confirmFamilyData) {
                setConfirmFamilySuccessData(confirmFamilyData);
                setConfirmAccountUiState(EConfirmAccountUiState.success);
            }
            if (prePopulateForm) {
                const {
                    userId,
                    code,
                    unconfirmedLinkId,
                    firstName,
                    lastName,
                    email,
                    password,
                    countryCode,
                    confirmingUser,
                } = prePopulateForm;
                setConfirmingUser(confirmingUser);
                setUserId(userId);
                setCode(code);
                setUnconfirmedLinkId(unconfirmedLinkId);
                setFirstName(firstName);
                setLastName(lastName);
                setConfirmingUser(confirmingUser);
                setEmail(email);
                setCountryCode(countryCode);
                setPassword(password);
                setDataIsPrePopulated(true);
                setConfirmAccountUiState(EConfirmAccountUiState.initialized);
            }
        } else {
            setConfirmAccountUiState(EConfirmAccountUiState.routerParsed);
        }
    };

    const getInitialData = async () => {
        !countriesList.length && (await getCountriesList());
        try {
            const response = await API.get.startConfirmAccount(
                `?linkId=${unconfirmedLinkId}&code=${code}&userId=${userId}`,
            );
            const { firstName, lastName, email, countryCode } = response.data;
            setConfirmingUser({ firstName, lastName });
            setFirstName(firstName);
            setLastName(lastName);
            setEmail(email);
            setCountryCode(countryCode);
            setConfirmAccountUiState(EConfirmAccountUiState.initialized);
        } catch {
            console.error('Failed to start confirming account');
            setConfirmAccountUiState(EConfirmAccountUiState.fatalError);
        }
    };

    // ----------
    // Form Validation
    // ----------
    const checkFirstNameIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!firstName.length) {
            isValid = false;
            validationMessage = language.validation.firstName;
        } else if (firstName.length > maxNameLength) {
            isValid = false;
            validationMessage = language.validation.firstNameMaxLength;
        }
        setFirstNameIsValid(isValid);
        setFirstNameValidationMessage(validationMessage);
        return isValid;
    };

    const checkLastNameIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!lastName.length) {
            isValid = false;
            validationMessage = language.validation.lastName;
        }
        if (lastName.length > maxNameLength) {
            isValid = false;
            validationMessage = language.validation.lastNameMaxLength;
        } else if (lastName.length > maxNameLength) {
            isValid = false;
            validationMessage = language.validation.lastNameMaxLength;
        }
        setLastNameIsValid(isValid);
        setLastNameValidationMessage(validationMessage);
        return isValid;
    };

    const checkPasswordIsValid = () => {
        const isValid = validatePassword(password);
        setPasswordIsValid(isValid);
        return isValid;
    };

    const checkCountryIsValid = () => {
        const isValid = countryCode ? true : false;
        setCountryIsValid(isValid);
        return isValid;
    };

    const validateForm = () => {
        const firstNameIsValid = checkFirstNameIsValid();
        const lastNameIsValid = checkLastNameIsValid();
        const passwordIsValid = checkPasswordIsValid();
        const countryIsValid = checkCountryIsValid();
        if (firstNameIsValid && lastNameIsValid && passwordIsValid && countryIsValid) {
            return true;
        }
        return false;
    };

    // ------------
    // Actions
    // ------------
    const submitConfirmAccount = async () => {
        const formIsValid = validateForm();
        if (formIsValid) {
            const postObject: TCompleteConfirmAccountPost = {
                code,
                firstName,
                lastName,
                password,
                countryCode,
                unconfirmedLinkId,
            };
            try {
                setConfirmAccountUiState(EConfirmAccountUiState.loading);
                const response = await API.post.completeConfirmAccount(userId, postObject);
                const { userBusinesses, legacyManualSyncToken } = response.data;
                const businesses: TBusiness[] = userBusinesses.map((business) => ({
                    users: business.users,
                    name: business.subscriberName,
                    city: business.city,
                    stateOrProvince: business.stateOrProvinceCode,
                    countryCode: business.countryCode,
                    subscriberId: business.subscriberId,
                }));
                setBusinesses(businesses);
                setLegacySyncToken(legacyManualSyncToken);
                setConfirmAccountUiState(EConfirmAccountUiState.success);
            } catch {
                setConfirmAccountUiState(EConfirmAccountUiState.softError);
                console.error('Failed to confirm account');
            }
        }
    };

    const familyFirstAddFamily = () => {
        const formIsValid = validateForm();
        if (formIsValid) {
            addFamily();
        }
    };

    const addFamily = () => {
        const userData = {
            userId,
            code,
            unconfirmedLinkId,
            firstName,
            lastName,
            email,
            password,
            countryCode,
            confirmingUser,
        };
        const confirmAccountRouterState: TConfirmAccountRouterState = {
            prePopulateForm: userData,
        };
        const createFamilyAccountRouterState: TCreateFamilyAccountRouterState = {
            confirmingUser,
            code,
            unconfirmedLinkId,
            familyAccountType: EFamilyAccountType.confirmAccount,
            primaryUser: { firstName, lastName, password, email, id: userId, country: countryCode },
        };
        // replace history state to preserve form values when navigating via back button
        history.replace({
            state: confirmAccountRouterState,
        });
        // then go to crete family account screen
        history.push({
            pathname: ERoutes.createFamilyAccount,
            state: createFamilyAccountRouterState,
        });
    };

    useEffect(() => {
        switch (confirmAccountUiState) {
            case EConfirmAccountUiState.default:
                parseRouterState();
                break;
            case EConfirmAccountUiState.routerParsed:
                parseQueryString();
                break;
            case EConfirmAccountUiState.queryStringParsed:
                getInitialData();
                break;
            case EConfirmAccountUiState.initialized:
                if (subscriberId) {
                    getCoBranding().then(() => {
                        setConfirmAccountUiState(EConfirmAccountUiState.ready);
                    });
                } else {
                    setConfirmAccountUiState(EConfirmAccountUiState.ready);
                }
                break;
            default:
                break;
        }
    }, [confirmAccountUiState]);

    useEffect(() => {
        if (!countryCode && countriesList.length) {
            setCountryCode(countriesList[0].code);
        }
    }, [countriesList]);

    return (
        <ConfirmAccountContext.Provider
            value={{
                confirmAccountUiState,
                userId,
                firstName,
                setFirstName,
                firstNameIsValid,
                setFirstNameIsValid,
                firstNameValidationMessage,
                lastName,
                lastNameIsValid,
                setLastNameIsValid,
                lastNameValidationMessage,
                setLastName,
                email,
                password,
                setPassword,
                passwordIsValid,
                countryCode,
                setCountryCode,
                countryIsValid,
                businesses,
                confirmingUser,
                submitConfirmAccount,
                errorMessage,
                legacySyncToken,
                addFamily,
                familyFirstAddFamily,
                confirmFamilySuccessData,
                dataIsPrePopulated,
            }}
        >
            {children}
        </ConfirmAccountContext.Provider>
    );
};

export default ConfirmAccountContext;
